<template>
  <div class="monitoring-dashboard-index">
    <p class="monitoring-dashboard-index__subtitle">
      {{ $t('monitoringDashboard.subtitle') }}
    </p>

    <MonitoringFilters
      v-model="filters"
      :loading="loading"
      :hide-reparto="
        showAcceptance && !showExpired && !showClosing && !showAwaiting
      "
      :can-export="canExport"
      @refresh="refresh"
      @clear="clearFilters"
      @export="debouncedExport"
    />
    <!--
      Blocks self-arrange via flex-wrap + flex-grow: each item targets ~50%
      width (so up to two per row) but grows to fill the row when fewer blocks
      are visible.
    -->
    <div class="dashboard-grid">
      <div
        v-if="showAcceptance"
        class="dashboard-grid__item"
      >
        <PendingAcceptanceBlock
          :definition="acceptanceDef"
          :data="acceptanceData"
          urgency="warning"
          :loading="loading"
          @drill="handleDrill"
        />
      </div>
      <div
        v-if="showExpired"
        class="dashboard-grid__item"
      >
        <MonitoringStatCardBlock
          :definition="expiredDef"
          :data="expiredData"
          urgency="critical"
          :loading="loading"
          @drill="handleDrill"
        />
      </div>
      <div
        v-if="showClosing"
        class="dashboard-grid__item"
      >
        <MonitoringMatrixBlock
          :definition="closingDef"
          :data="closingData"
          urgency="warning"
          :loading="loading"
          @drill="handleDrill"
        />
      </div>
      <div
        v-if="showAwaiting"
        class="dashboard-grid__item"
      >
        <AwaitingProcessingBlock
          :definition="awaitingProcessingDef"
          :data="awaitingData"
          :kpis="awaitingKpis"
          urgency="safe"
          :loading="loading"
          @drill="handleDrill"
        />
      </div>
    </div>

    <MonitoringDetailPanel
      v-if="showDetail"
      ref="detail"
      :filters="canonicalFilters"
      @open-sample="openSample"
      @close="showDetail = false"
    />
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import debounce from 'lodash.debounce';
  import { monitoringApi } from '@/api';
  import acl from '@/services/acl';
  import {
    ACCEPTANCE_CARDS,
    EXPIRED_DEF,
    CLOSING_DEF,
    MONITORING_BLOCKS,
    MONITORING_PERMISSIONS,
    buildMonitoringParams,
    normalizeClosingData,
    normalizeAwaitingRows,
  } from '@/config/monitoringDashboard.config';
  import MonitoringFilters from './MonitoringFilters.vue';
  import MonitoringStatCardBlock from './MonitoringStatCardBlock.vue';
  import MonitoringMatrixBlock from './MonitoringMatrixBlock.vue';
  import AwaitingProcessingBlock from './AwaitingProcessingBlock.vue';
  import MonitoringDetailPanel from './MonitoringDetailPanel.vue';
  import PendingAcceptanceBlock from './PendingAcceptanceBlock.vue';

  const FILTER_KEY = 'monitoringDashboard';

  function emptyFilters() {
    return { sede: [], reparto: [] };
  }

  function normalizeSede(sede) {
    if (Array.isArray(sede)) return sede;
    return sede ? [sede] : [];
  }

  export default {
    name: 'MonitoringDashboardIndex',
    components: {
      MonitoringFilters,
      MonitoringStatCardBlock,
      MonitoringMatrixBlock,
      AwaitingProcessingBlock,
      MonitoringDetailPanel,
      PendingAcceptanceBlock,
    },
    data() {
      return {
        filters: emptyFilters(),
        acceptanceDef: ACCEPTANCE_CARDS,
        expiredDef: EXPIRED_DEF,
        closingDef: CLOSING_DEF,
        awaitingProcessingDef: {
          blockKey: MONITORING_BLOCKS.awaitingProcessing,
          titleKey: 'monitoringDashboard.blocks.toBeProcessed.title',
        },
        acceptanceData: {},
        expiredData: {},
        closingData: {},
        awaitingData: [],
        awaitingKpis: {},
        showDetail: false,
        loading: false,
        debouncedExport: null,
      };
    },
    created() {
      this.debouncedExport = debounce(this.exportExcel, 300);
      this.hydrateFilters();
      this.refresh();
    },
    beforeDestroy() {
      this.debouncedExport.cancel();
    },
    computed: {
      ...mapGetters(['filtersStatus']),
      canAcceptance() {
        return acl.userCanUse(
          MONITORING_PERMISSIONS[MONITORING_BLOCKS.acceptance]
        );
      },
      canDefault() {
        return acl.userCanUse(
          MONITORING_PERMISSIONS[MONITORING_BLOCKS.expired]
        );
      },
      canExport() {
        return this.pageBlocks.some((block) =>
          acl.userCanUse(MONITORING_PERMISSIONS[block])
        );
      },
      // A block renders only if it belongs to this page AND the
      // user holds the matching permission.
      pageBlocks() {
        return this.$route.meta.monitoringBlocks || [];
      },
      showAcceptance() {
        return (
          this.pageBlocks.includes(MONITORING_BLOCKS.acceptance) &&
          this.canAcceptance
        );
      },
      showExpired() {
        return (
          this.pageBlocks.includes(MONITORING_BLOCKS.expired) && this.canDefault
        );
      },
      showClosing() {
        return (
          this.pageBlocks.includes(MONITORING_BLOCKS.closing) && this.canDefault
        );
      },
      showAwaiting() {
        return (
          this.pageBlocks.includes(MONITORING_BLOCKS.awaitingProcessing) &&
          this.canDefault
        );
      },
      // Filters in the shape the API / where builders expect.
      canonicalFilters() {
        return {
          locationIds: (this.filters.sede || []).map((s) => s.value),
          laboratories: this.filters.reparto || [],
        };
      },
    },
    methods: {
      ...mapActions(['applyFilters', 'resetFilters']),
      hydrateFilters() {
        const stored = this.filtersStatus[FILTER_KEY] || {};
        this.filters = {
          sede: normalizeSede(stored.sede),
          reparto: stored.reparto || [],
        };
      },
      async refresh() {
        this.applyFilters({ filterKey: FILTER_KEY, filterData: this.filters });

        this.loading = true;
        try {
          const requests = [];
          // Block 1 is Sede-only (omit reparto); the rest pass both filters.
          const params = buildMonitoringParams(this.canonicalFilters, true);

          if (this.showAcceptance) {
            requests.push(
              monitoringApi
                .pendingAcceptance(
                  buildMonitoringParams(this.canonicalFilters, false)
                )
                .then((res) => {
                  this.acceptanceData = res.data;
                })
            );
          }

          if (this.showExpired) {
            requests.push(
              monitoringApi.expired(params).then((res) => {
                this.expiredData = res.data;
              })
            );
          }

          if (this.showClosing) {
            requests.push(
              monitoringApi.closingDue(params).then((res) => {
                this.closingData = normalizeClosingData(res.data);
              })
            );
          }

          if (this.showAwaiting) {
            requests.push(
              monitoringApi.awaitingProcessing(params).then((res) => {
                const { rows, kpis } = normalizeAwaitingRows(res.data);
                this.awaitingData = rows;
                this.awaitingKpis = kpis;
              })
            );
          }

          await Promise.all(requests);

          // Close any open drill-down: its context (filter + counts) is now stale.
          this.showDetail = false;
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message:
              (error.response &&
                error.response.data &&
                error.response.data.error &&
                error.response.data.error.message) ||
              error.message,
          });
        } finally {
          this.loading = false;
        }
      },
      clearFilters() {
        this.filters = emptyFilters();
        this.resetFilters(FILTER_KEY);
        this.refresh();
      },
      handleDrill(drill) {
        this.showDetail = true;
        this.$nextTick(() => {
          if (this.$refs.detail) this.$refs.detail.openDrill(drill);
        });
      },
      openSample(row, drill) {
        const { blockKey } = drill;
        // Acceptance → user lands on the acceptance workflow
        // rather than data entry. Every other block opens data entry below.
        if (blockKey === MONITORING_BLOCKS.acceptance) {
          if (!row.orderId) return;
          this.$router
            .push({
              name: 'receivedSamplesIndex',
              query: {
                page: 1,
                filterKey: 'receivedSamples',
                filterData: JSON.stringify({
                  orderId: [{ key: row.orderId, label: String(row.orderId) }],
                }),
              },
            })
            .catch(() => {});
          return;
        }
        const labSampleId = row.id || row.labSampleId;
        if (!labSampleId) return;
        // "Completed unsigned" and "to be approved" rows aren't in
        // data entry → status management (gestione status) instead.
        const toStatusManagement =
          (blockKey === MONITORING_BLOCKS.expired &&
            ['completedunsigned', 'tobeapproved'].includes(drill.statusKey)) ||
          (blockKey === MONITORING_BLOCKS.closing &&
            ['tba', 'tbs'].includes(drill.statusKey));
        this.$router
          .push({
            name: toStatusManagement
              ? 'analysisStatusManagementIndex'
              : 'insertAnalysesDataIndex',
            query: {
              page: 1,
              filterKey: toStatusManagement
                ? 'analysesStatus'
                : 'insertAnalysesData',
              filterData: JSON.stringify({
                id: [{ key: labSampleId, label: row.labSampleCode }],
              }),
            },
          })
          .catch(() => {});
      },
      async exportExcel() {
        const exportParams =
          this.$refs.detail && this.$refs.detail.buildExportParams();

        if (!exportParams) {
          this.$notify({
            showClose: true,
            type: 'info',
            message: this.$t('monitoringDashboard.exportNoDetail'),
          });

          return;
        }
        try {
          const response = await monitoringApi.exportDetails(exportParams);
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'monitoraggio-scadenze.xlsx');
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message:
              (error.response &&
                error.response.data &&
                error.response.data.error &&
                error.response.data.error.message) ||
              error.message,
          });
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .monitoring-dashboard-index {
    width: 100%;

    &__subtitle {
      margin: 0 0 16px;
      color: #cbd5e1;
    }
  }

  .dashboard-grid {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    gap: 20px;
  }

  .dashboard-grid__item {
    flex: 1 1 calc(50% - 10px);
    min-width: 320px;
  }
</style>
